import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { EyeIcon, BookmarkSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { databaseMigrationsApi, type DryRunResult, type FolderMigration } from '../../api/databaseMigrations';
import SqlEditor from './components/SqlEditor';
import SavedScriptPicker from './components/SavedScriptPicker';
import DryRunPreviewModal from './components/DryRunPreviewModal';
import RunProgressPanel from './components/RunProgressPanel';
import RunHistoryTable from './components/RunHistoryTable';
import MigrationsList from './components/migrations/MigrationsList';
import BulkApplyModal from './components/migrations/BulkApplyModal';
import BulkRunDrawer from './components/migrations/BulkRunDrawer';
import MarkAppliedModal from './components/migrations/MarkAppliedModal';
import { useBulkRun } from './components/migrations/hooks/useBulkRun';
import type { MigrationRowData, TargetSummary } from './components/migrations/utils/constants';
import { deriveStatus } from './components/migrations/utils/statusDerivation';

type Completion = { status: 'success' | 'failed'; failedTarget?: string; succeededTargets: string[]; errorMessage?: string };

interface RegistryMigrationsTabProps {
  onNewCountChange?: (count: number) => void;
}

// The registry's one target name. The server writes and reads every registry
// run as '__registry__' (it used to be recorded under both this and
// 'cma_registry', so registry migrations that had run showed as new).
const REGISTRY_TARGET: TargetSummary = {
  id: '__registry__',
  name: 'Registry DB',
  databaseName: '__registry__',
  kind: 'registry' as const,
};

const AVAILABLE_TARGETS: TargetSummary[] = [REGISTRY_TARGET];
const REGISTRY_TARGETS = ['__registry__'];
const INITIAL_SELECTED = new Set(['__registry__']);

/** Convert a FolderMigration from the API to the new MigrationRowData shape. */
function toRowData(m: FolderMigration): MigrationRowData {
  const appliedTo: MigrationRowData['appliedTo'] = {};
  for (const [db, info] of Object.entries(m.appliedTo)) {
    const raw = info.status;
    // `resolved` = fixed by hand (counts as done); `partial` / `interrupted`
    // need attention like a failure — see the Status tab for the run.
    const status: 'success' | 'failed' | 'pending' =
      raw === 'success' || raw === 'resolved' ? 'success'
        : raw === 'failed' || raw === 'partial' || raw === 'interrupted' ? 'failed'
          : 'pending';
    // Carry the server's error classification through — without it a failure
    // caused by "the change is already there" is indistinguishable from a real
    // one, which is what made these migrations look permanently unapplied.
    appliedTo[db] = {
      version: info.version,
      lastRun: info.lastRun,
      status,
      errorKind: info.errorKind,
      errorReason: info.errorReason,
      errorDetail: info.errorDetail,
      errorCode: info.errorCode,
    };
  }
  return {
    id: m.scriptId,
    folderName: m.folder,
    fileName: m.filename,
    folderPath: m.folderPath,
    version: m.version,
    scope: m.scope,
    scopeInferred: m.scopeInferred,
    contentChanged: m.contentChangedSinceLast,
    hash: m.contentHash,
    appliedTo,
    sqlPreview: undefined,
    // Must test for a SUCCESS, not merely for a run. appliedTo now carries
    // failed targets too (so the UI can explain why), and counting those as
    // "ever applied" would mark a migration that has only ever failed as
    // applied whenever no target is selected.
    everApplied: Object.values(appliedTo).some((a) => a.status === 'success'),
    archived: m.archived,
    archivedAt: m.archivedAt,
    archivedReason: m.archivedReason,
  };
}

const RegistryMigrationsTab: React.FC<RegistryMigrationsTabProps> = (props) => {
  const [sql, setSql] = useState('');
  const [scriptId, setScriptId] = useState<number | null>(null);
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [completion, setCompletion] = useState<Completion | null>(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [savingName, setSavingName] = useState('');
  const [savingDesc, setSavingDesc] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  // Registry target is always selected
  const [selectedTargetIds, setSelectedTargetIds] = useState<Set<string>>(INITIAL_SELECTED);

  // Bulk run state
  const { state: bulkState, start: startBulkRun, cancel: cancelBulkRun, reset: resetBulkRun } = useBulkRun();
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<MigrationRowData[]>([]);
  const [bulkTargetIds, setBulkTargetIds] = useState<string[]>([]);

  // Mark as applied state
  const [markAppliedRow, setMarkAppliedRow] = useState<MigrationRowData | null>(null);

  // Folder migrations list (registry scope only)
  const [folderMigrations, setFolderMigrations] = useState<FolderMigration[]>([]);
  const refetchMigrations = useCallback(() => {
    databaseMigrationsApi.listFolderMigrations()
      .then((list) => setFolderMigrations(list.filter((m) => m.scope === 'registry')));
  }, []);
  useEffect(() => {
    refetchMigrations();
  }, [historyKey, refetchMigrations]);

  // Map FolderMigration[] to MigrationRowData[]
  const migrationRows: MigrationRowData[] = useMemo(
    () => folderMigrations.map(toRowData),
    [folderMigrations],
  );

  // Emit "new" count to parent for tab badge
  useEffect(() => {
    const count = migrationRows.filter((r) => !r.archived && deriveStatus(r, []) === 'new').length;
    props.onNewCountChange?.(count);
  }, [migrationRows, props.onNewCountChange]);

  const errMsg = (e: unknown): string => {
    if (axios.isAxiosError(e)) {
      return e.response?.data?.message || e.response?.data?.error || e.message;
    }
    return e instanceof Error ? e.message : String(e);
  };

  const handleDryRun = async () => {
    setActionError(null);
    try {
      const result = await databaseMigrationsApi.dryRun({ sql, targets: REGISTRY_TARGETS });
      setDryRun(result);
    } catch (e) {
      setActionError(`Dry-run failed: ${errMsg(e)}`);
    }
  };

  const handleExecute = async () => {
    setActionError(null);
    setDryRun(null);
    try {
      const id = await databaseMigrationsApi.run({ sql, targets: REGISTRY_TARGETS, backup: false, scriptId });
      setRunId(id);
    } catch (e) {
      setActionError(`Execute failed: ${errMsg(e)}`);
    }
  };

  const handleSaveScript = async () => {
    if (!savingName || !sql) return;
    await databaseMigrationsApi.createScript({
      name: savingName, description: savingDesc, sql_content: sql, target_scope: 'registry',
    });
    setShowSaveDialog(false);
    setSavingName('');
    setSavingDesc('');
  };

  const handleApplyMigration = useCallback(
    (row: MigrationRowData, targetIds: string[]) => {
      if (targetIds.length === 0) return;
      setBulkRows([row]);
      setBulkTargetIds(targetIds);
      setBulkDrawerOpen(true);
      startBulkRun([row.id], targetIds, { backup: false });
    },
    [startBulkRun],
  );

  const handlePreviewMigration = async (row: MigrationRowData) => {
    setActionError(null);
    try {
      const { script } = await databaseMigrationsApi.getScript(row.id);
      setSql(script.sql_content);
      setScriptId(row.id);
    } catch (e) {
      setActionError(errMsg(e));
    }
  };

  const handleBulkApply = useCallback((rows: MigrationRowData[], targetIds: string[]) => {
    setBulkRows(rows);
    setBulkTargetIds(targetIds);
    setBulkModalOpen(true);
  }, []);

  const handleBulkConfirm = useCallback(
    async ({ mode, backup, reason }: { mode: 'run' | 'mark'; backup: boolean; reason: string }) => {
      setBulkModalOpen(false);
      if (mode === 'mark') {
        try {
          const migrationIds = bulkRows.map((r) => r.id);
          const targetDbs = Array.from(bulkTargetIds);
          await databaseMigrationsApi.markApplied(migrationIds, targetDbs, reason);
          await refetchMigrations();
        } catch (err) {
          console.error('Bulk mark-applied failed:', err);
          alert(err instanceof Error ? err.message : 'Bulk mark-applied failed');
        }
      } else {
        setBulkDrawerOpen(true);
        const migrationIds = bulkRows.map((r) => r.id);
        startBulkRun(migrationIds, bulkTargetIds, { backup });
      }
    },
    [bulkRows, bulkTargetIds, startBulkRun, refetchMigrations],
  );

  const handleMarkApplied = useCallback((row: MigrationRowData) => {
    setMarkAppliedRow(row);
  }, []);

  // Auto-refetch migration list when a bulk run (including single-apply) finishes
  useEffect(() => {
    if (bulkState.status === 'complete' || bulkState.status === 'aborted' || bulkState.status === 'failed') {
      void refetchMigrations();
    }
  }, [bulkState.status, refetchMigrations]);

  const handleCopyReport = useCallback(() => {
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
    const successEvents = bulkState.events.filter((e) => e.type === 'success');
    const failureEvents = bulkState.events.filter((e) => e.type === 'failure');
    const lines: string[] = [
      `Bulk migration run — ${now}`,
      `${bulkState.completedOperations} of ${bulkState.totalOperations} migrations x ${bulkTargetIds.length} targets applied`,
      '',
    ];
    for (const ev of successEvents) {
      const row = bulkRows.find((r) => r.id === ev.migrationId);
      lines.push(`[OK] ${row?.folderName ?? ev.migrationId} -> Registry DB`);
    }
    for (const ev of failureEvents) {
      const row = bulkRows.find((r) => r.id === ev.migrationId);
      lines.push(`[FAIL] ${row?.folderName ?? ev.migrationId} -> Registry DB — ${ev.error ?? 'unknown error'}`);
    }
    navigator.clipboard.writeText(lines.join('\n')).catch(() => undefined);
  }, [bulkState, bulkRows, bulkTargetIds]);

  return (
    <div className="px-6 py-6 space-y-5">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
        <div className="text-sm text-red-800">
          <strong>Registry DB writes affect the entire platform.</strong> Every change here requires typed confirmation, regardless of statement type. There is no automatic backup option for the registry — take a manual <code>mysqldump</code> first if you need rollback capability.
        </div>
      </div>

      <SavedScriptPicker scope="registry" onLoad={(id, content) => { setScriptId(id); setSql(content); }} />
      <SqlEditor value={sql} onChange={(v) => { setSql(v); setScriptId(null); }} />
      <MigrationsList
        tabKind="registry"
        rows={migrationRows}
        availableTargets={AVAILABLE_TARGETS}
        selectedTargetIds={Array.from(selectedTargetIds)}
        onTargetsChange={(ids) => setSelectedTargetIds(new Set(ids))}
        onApplyMigration={handleApplyMigration}
        onPreviewMigration={handlePreviewMigration}
        onBulkApply={handleBulkApply}
        onMarkApplied={handleMarkApplied}
        onArchiveChanged={refetchMigrations}
      />

      <div className="flex items-center gap-2">
        <button
          onClick={handleDryRun}
          disabled={!sql.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <EyeIcon className="w-4 h-4" /> Dry-run preview
        </button>
        <button
          onClick={() => setShowSaveDialog(true)}
          disabled={!sql.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
        >
          <BookmarkSquareIcon className="w-4 h-4" /> Save as script
        </button>
      </div>

      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-red-800">
            <strong className="block mb-0.5">Server returned an error</strong>
            <code className="text-xs break-words whitespace-pre-wrap">{actionError}</code>
          </div>
          <button
            onClick={() => setActionError(null)}
            className="text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {runId && !completion && (
        <RunProgressPanel
          key={runId}
          runId={runId}
          targets={REGISTRY_TARGETS}
          onComplete={(c) => { setCompletion(c); setHistoryKey((k) => k + 1); }}
        />
      )}

      <RunHistoryTable scriptId={scriptId ?? undefined} refreshKey={historyKey} />

      {dryRun && (
        <DryRunPreviewModal
          result={dryRun}
          targets={REGISTRY_TARGETS}
          isRegistry
          onClose={() => setDryRun(null)}
          onConfirm={handleExecute}
        />
      )}

      {showSaveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3">
            <h3 className="text-lg font-semibold text-slate-900">Save registry migration script</h3>
            <input
              type="text" value={savingName} onChange={(e) => setSavingName(e.target.value)}
              placeholder="Name"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            <textarea
              value={savingDesc} onChange={(e) => setSavingDesc(e.target.value)}
              placeholder="Description (optional)" rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowSaveDialog(false)} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleSaveScript} disabled={!savingName} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk apply confirmation modal */}
      <BulkApplyModal
        open={bulkModalOpen}
        rows={bulkRows}
        targets={AVAILABLE_TARGETS.filter((t) => bulkTargetIds.includes(t.id))}
        onCancel={() => setBulkModalOpen(false)}
        onConfirm={handleBulkConfirm}
      />

      {/* Bulk run progress drawer */}
      <BulkRunDrawer
        open={bulkDrawerOpen}
        rows={bulkRows}
        targets={AVAILABLE_TARGETS.filter((t) => bulkTargetIds.includes(t.id))}
        state={bulkState}
        onClose={() => {
          if (bulkState.status === 'running') cancelBulkRun();
          setBulkDrawerOpen(false);
          resetBulkRun();
        }}
        onCopyReport={handleCopyReport}
      />

      {/* Mark as applied confirmation modal */}
      <MarkAppliedModal
        open={markAppliedRow !== null}
        row={markAppliedRow}
        targets={AVAILABLE_TARGETS.filter((t) => selectedTargetIds.has(t.id))}
        onCancel={() => setMarkAppliedRow(null)}
        onConfirm={async (reason) => {
          if (!markAppliedRow) return;
          const targetDbs = AVAILABLE_TARGETS
            .filter((t) => selectedTargetIds.has(t.id))
            .map((t) => t.databaseName);
          try {
            await databaseMigrationsApi.markApplied([markAppliedRow.id], targetDbs, reason);
            await refetchMigrations();
          } catch (err) {
            console.error('Mark applied failed:', err);
            alert(err instanceof Error ? err.message : 'Failed to mark migration as applied');
          } finally {
            setMarkAppliedRow(null);
          }
        }}
      />
    </div>
  );
};

export default RegistryMigrationsTab;
